import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';

import { useInvalidateIntegrationByAccountId } from '@/entities/integrations';
import { deleteIntegrationById } from '@/shared/api/integrations';
import { useGetConfirmation } from '@/shared/ui/Confirmations/confirmation';
import { useNotifications } from '@/shared/ui/Notifications';

import type { IIntegration } from '@/entities/integrations';

interface DeleteErrorData {
  error?: unknown;
  details?: unknown;
}

interface DeleteErrorShape {
  message?: string;
  data?: DeleteErrorData;
}

// The backend rejects deletion with Hono responses like { error: 'integration_is_active' }.
// ApiError puts that code into data.error, while error.message stays the generic
// 'Произошла ошибка' title — so the mapping must key on data.error, not message.
export const resolveDeleteErrorCaption = (
  error: unknown,
  t: (key: string) => string
): string => {
  const shaped = error as DeleteErrorShape | null;
  const code = typeof shaped?.data?.error === 'string' ? shaped.data.error : '';
  const messages: Record<string, string> = {
    integration_is_active: t('integrations:delete.errors.active'),
  };
  if (code && messages[code]) return messages[code];

  const details = shaped?.data?.details;
  return typeof details === 'string' && details
    ? details
    : t('common:errors.generic');
};

export function useDeleteIntegration() {
  const { t } = useTranslation();
  const invalidateIntegrationsList = useInvalidateIntegrationByAccountId();
  const { getConfirmation, setPending, closeConfirmation } =
    useGetConfirmation();
  const { addNotification } = useNotifications();

  const deleteIntegrationMutation = useMutation({
    mutationFn: (integraId: string) => deleteIntegrationById(integraId),
    onSuccess: async () => {
      closeConfirmation();
      addNotification({ title: t('integrations:delete.success') });
      await invalidateIntegrationsList();
    },
    onError: (error) => {
      setPending(false);
      addNotification({
        title: t('integrations:delete.failed'),
        caption: resolveDeleteErrorCaption(error, t),
      });
    },
  });

  const deleteIntegration = async (integration: IIntegration): Promise<void> => {
    const isConfirm = await getConfirmation({
      shouldCloseOnConfirm: false,
      title: t('integrations:delete.dialog.title'),
      description: t('integrations:delete.dialog.description', {
        name: integration.crm_domain || integration.crm_account_id,
      }),
      confirmText: t('integrations:delete.dialog.confirm'),
      priorityButton: 'reject',
    });

    if (!isConfirm) return;

    setPending(true);
    await deleteIntegrationMutation.mutateAsync(integration.integra_id!);
  };

  return {
    deleteIntegration,
    ...deleteIntegrationMutation,
  };
}
